import { createFileRoute } from "@tanstack/react-router";
import { useGetSiteInspectionSchedule } from "@/api/client-inspection";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { ScheduleForm } from "@/sections/workspace/inspection-schedule/schedule-form";
import { useSelectedCTESiteId } from "@/stores/site-store";

export const Route = createFileRoute(
  "/dashboard/workspace/inspection-schedule",
)({
  component: InspectionSchedulePage,
  staticData: {
    title: "Inspection schedule",
  },
});

/**
 * When the next inspection falls due at the selected site, and at what grade
 * (design/inspection-schedule/Main.dc.html). One form for the whole page; the
 * floating bar saves it as one PUT.
 */
function InspectionSchedulePage() {
  const siteId = useSelectedCTESiteId();

  if (!siteId) {
    return (
      <p className="text-muted-foreground text-sm">
        Choose a site to see its inspection schedule.
      </p>
    );
  }

  return <SiteSchedule siteId={siteId} />;
}

function SiteSchedule({ siteId }: { siteId: string }) {
  const { canAccess } = useAuth();
  const canChange = canAccess("manager", siteId);

  const {
    data: settings,
    isLoading,
    error,
    refetch,
  } = useGetSiteInspectionSchedule(siteId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <PageStatus
        variant="error"
        title="Failed to load the inspection schedule"
        description="There was a problem reaching the server. Try again."
        actions={<Button onClick={() => refetch()}>Try again</Button>}
      />
    );
  }

  return (
    <ScheduleForm
      key={`${siteId}:${settings.updatedAt ?? "default"}`}
      siteId={siteId}
      schedule={settings.schedule}
      isDefault={settings.isDefault}
      updatedAt={settings.updatedAt}
      updatedBy={settings.updatedBy}
      canChange={canChange}
    />
  );
}
